/**
 * pre197: Gemeinsame Kamera-Steuerung — dieselbe Optik wie in der Berateransicht
 * (Licht-Button mit Icon, Steuerkreuz mit Scan/Snapshot in der Mitte, Zoom-Slider,
 * Kamerawechsel). Rein darstellend: Zustand + Callbacks kommen als Props, damit A/V-
 * Sitzung und Companion dieselbe UI mit ihrem jeweiligen Kanal verdrahten können.
 * Rendert NICHTS, wenn die Funktion nicht verfügbar ist (`available=false`).
 */

export interface CameraSpec { role: string; label: string }

interface CameraControlsProps {
  available: boolean;
  lightOn: boolean;
  onToggleLight: () => void;
  zoom: number;
  zoomMax?: number;
  onZoom: (factor: number) => void;   // absoluter Zoomfaktor (Slider)
  onMove: (dx: number, dy: number) => void;
  onReset: () => void;
  onSnapshot: () => void;
  scanning?: boolean;
  onSwapCamera?: () => void;
  meshFollow?: boolean;
  onMeshFollow?: (v: boolean) => void;
}

function chevron(points: string) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points={points} />
    </svg>
  );
}

export function CameraControls(props: CameraControlsProps) {
  const {
    available, lightOn, onToggleLight, zoom, zoomMax = 4, onZoom, onMove, onReset,
    onSnapshot, scanning = false, onSwapCamera, meshFollow, onMeshFollow,
  } = props;
  if (!available) return null;
  const isZoomed = zoom > 1.01;
  const padBtn = (dx: number, dy: number, title: string, pts: string) => (
    <button
      className="insp-btn"
      style={{ width: 30, height: 30, borderRadius: 8, opacity: isZoomed ? 1 : 0.35, cursor: isZoomed ? 'pointer' : 'not-allowed' }}
      onClick={() => onMove(dx, dy)}
      disabled={!isZoomed}
      title={title}
    >
      {chevron(pts)}
    </button>
  );

  return (
    <div style={{ marginTop: 18, borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
      {/* Kopf: Label + Kamerawechsel */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600 }}>Kamera</span>
        {onSwapCamera && (
          <button onClick={onSwapCamera} title="Haupt-/Dokumentenkamera tauschen"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer', color: 'var(--fg-soft)', fontFamily: 'var(--font)', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2l-3-3 3-3v2h6v-2l3 3-3 3z" /></svg>
            Kamera tauschen
          </button>
        )}
      </div>

      {/* Licht-Button + Steuerkreuz */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'center' }}>
        <button
          onClick={onToggleLight}
          title={lightOn ? 'Dokumentenlicht aus' : 'Dokumentenlicht an'}
          style={{
            width: 44, height: 44, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${lightOn ? 'var(--tenant-accent)' : 'var(--hairline)'}`,
            background: lightOn ? 'var(--tenant-accent-soft)' : 'var(--bg-elev-2)',
            color: lightOn ? 'var(--tenant-accent)' : 'var(--muted)',
          }}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18h6" /><path d="M10 22h4" />
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
          </svg>
        </button>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '30px 34px 30px',
            gridTemplateRows: '30px 34px 30px',
            gap: 4, justifyContent: 'center', alignItems: 'center',
          }}
        >
          <span />
          {padBtn(0, -0.05, 'Hoch', '6 15 12 9 18 15')}
          <span />
          {padBtn(-0.05, 0, 'Links', '15 6 9 12 15 18')}
          <button
            onClick={onSnapshot}
            disabled={scanning}
            title="Scan / Snapshot"
            style={{
              width: 34, height: 34, borderRadius: 8, cursor: scanning ? 'wait' : 'pointer',
              background: 'var(--tenant-accent)', color: '#fff', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.4)', opacity: scanning ? 0.6 : 1,
            }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="var(--tenant-accent)" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 13.2 L8.2 6.9 L7.5 5.5 L21 11.7 Z" />
              <rect x="3" y="13.5" width="18" height="5" rx="1" />
              <rect x="7.5" y="12" width="9" height="1.6" rx="0.3" />
            </svg>
          </button>
          {padBtn(0.05, 0, 'Rechts', '9 6 15 12 9 18')}
          <span />
          {padBtn(0, 0.05, 'Runter', '6 9 12 15 18 9')}
          <span />
        </div>
      </div>

      {/* Zoom-Slider + Reset */}
      <div className="tx-conf-slider" style={{ marginTop: 14 }}>
        <span style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600, width: 34 }}>Zoom</span>
        <input
          type="range"
          min={1}
          max={zoomMax}
          step={0.1}
          value={Math.min(zoomMax, Math.max(1, zoom))}
          onChange={(e) => onZoom(parseFloat(e.target.value))}
        />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', width: 34, textAlign: 'right' }}>{zoom.toFixed(1)}×</span>
        <button
          onClick={onReset}
          disabled={!isZoomed}
          title="Zoom zurücksetzen"
          className="insp-btn"
          style={{ width: 30, height: 30, borderRadius: 8, opacity: isZoomed ? 1 : 0.35, cursor: isZoomed ? 'pointer' : 'not-allowed' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.5 2.8L3 8" /><path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      {scanning && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>Scan läuft …</div>
      )}
      {onMeshFollow !== undefined && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: 'var(--muted)', marginTop: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!meshFollow} onChange={(e) => onMeshFollow(e.target.checked)} style={{ marginTop: 1 }} />
          <span><b>Dritte Teilnehmer sehen meine Kameraauswahl</b> — beim Wechsel (Klick aufs kleine Bild)
          folgt auch das Bild für per Link zugeschaltete Teilnehmer. Aus: Dritte behalten das Porträt-Bild.</span>
        </label>
      )}
      <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 8 }}>
        Zoom/Scan werden auf der Stele ausgeführt; das Ergebnis erscheint hier bzw. am A/V-Gerät.
      </div>
    </div>
  );
}
